<template>
  <div
    :class="{ 'text-normal-reduced': unavailable, 'bg-surface-level-1': isExpanded, 'bg-surface-level-0': !isExpanded }"
    class="w-full border-b border-border-light py-3">
    <Accordion :data-testId="`stop-${props.stop.order}`" :show-info="showInfo" :is-open="isExpanded"
      @expansionChange="handleExpansionChange">
      <template #header="{ isOpen }">
        <div class="w-full">
          <!-- First Row: Order, Name, and Options -->
          <div class="flex justify-between items-center">
            <div class="font-semibold flex items-center gap-2">
              <span :class="unavailable ? 'text-normal-reduced' : 'text-normal-emphasis'">{{ props.stop.order }}. {{
                props.stop.name }}</span>
            </div>
            <div v-if="showOptions" class="flex gap-2" @click.prevent.stop>
              <AppOptionMenu @click="handleMenuAction" :menuItems="menuItems" />
            </div>
            <div v-if="showCheckedIn" class="flex gap-2">
              <i v-if="props.checkedInStatus === 'checkedIn'" class="fas fa-check-circle text-confirm"></i>
              <i v-else-if="props.checkedInStatus === 'notCheckedIn'" class="far fa-circle text-confirm"></i>
              <i v-else class="fas fa-circle-xmark text-critical-text"></i>
            </div>
          </div>
        </div>
      </template>

      <template #headerSecondary="{ isOpen }">
        <div class="w-full">
          <div class="flex flex-col text-sm ml-6">
            <div class="flex items-center gap-2">
              <i class="far fa-clock text-normal-reduced"></i>
              <span :class="unavailable ? 'text-normal-reduced' : 'text-normal-emphasis'"
                class=" font-semibold">Arrival:</span>
              <span class="font-medium">
                {{ formatDate(stop.expectedArrivalTime) }}
              </span>
            </div>
            <div class="flex items-center gap-2 pl-[20px]" v-if="stop.expectedCardDealTime && cardReleaseType === 'schedule'">
              <span :class="unavailable ? 'text-normal-reduced' : 'text-normal-emphasis'"
                class="font-semibold">Card Dealt at:</span>
              <span class="font-medium">
                {{ formatDate(stop.expectedCardDealTime) }}
              </span>
            </div>
            <div class="flex items-center gap-2 pl-[20px]">
              <span :class="unavailable ? 'text-normal-reduced' : 'text-normal-emphasis'"
                class="font-semibold">Departure:</span>
              <span class="font-medium">
                {{ formatDate(stop.expectedDepartureTime) }}
              </span>
            </div>
            <div v-if="isOverlapping" class="mt-2">
              <span class="text-critical-text">This stop overlaps with another stop</span>
            </div>
            <div v-if="checkedInStatus === 'missedCheckIn'" class="mt-2">
              <span class="text-normal-reduced">Card(s) no longer available</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Expanded View -->
      <template #default>
        <div class="flex flex-col text-sm ml-6">
          <AppAddressDisplay :address="stop.address" :showName="false" />

          <div v-if="showMap" class="flex">
            <div class="w-[20px]">

            </div>
            <AppLink @click="toggleShowMap" class="text-action text-sm mt-1">
              <span>Show map </span>
              <i class="fas" :class="internalShowMap ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </AppLink>
          </div>

          <div v-if="showMap && internalShowMap && stop.address.location?._lat" class="mt-2">
            <Map class="my-2" :lat="stop.address?.location?._lat" :lng="stop.address?.location?._long" :name="stop.name"
              :tag="`stop${stop.order}`" />
          </div>
        </div>
      </template>
    </Accordion>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Stop } from '@models/Stop';
import Accordion from '@/components/Accordion.vue';
import AppOptionMenu from '@/components/AppOptionMenu.vue';
import { useRouter } from 'vue-router';
import { format } from 'date-fns';
import Map from '@/components/Map.vue';
import AppAddressDisplay from '@/components/AppAddressDisplay.vue';
import AppLink from '@/components/AppLink.vue';

const router = useRouter();

interface Props {
  stop: Stop;
  showDate?: boolean;
  showOptions?: boolean;
  isOverlapping?: boolean;
  expanded?: boolean;
  showMap: boolean;
  isCurrentStop?: boolean;
  showCheckedIn?: boolean;
  checkedInStatus?: "checkedIn" | "notCheckedIn" | "missedCheckIn" | "skippedCheckin";
  cardReleaseType?: "manual" | "schedule" | "geo";
}

const props = withDefaults(defineProps<Props>(), {
  showDate: false,
  showOptions: false,
  isOverlapping: false,
  expanded: false,
  isCurrentStop: false,
  showMap: true,
  showCheckedIn: false,
  checkedInStatus: "notCheckedIn",
  cardReleaseType: "manual"
});

const unavailable = computed(() => props.checkedInStatus === "checkedIn" || props.checkedInStatus === "missedCheckIn");

const emit = defineEmits(['editStop', 'deleteStop', 'update:expanded']);

const showInfo = ref(false);
const isExpanded = ref(props.expanded);

watch(() => props.expanded, (newValue) => {
  isExpanded.value = newValue;
});

const handleExpansionChange = (expanded: boolean) => {
  isExpanded.value = expanded;
  emit('update:expanded', expanded);
};

const menuItems = [
  {
    label: 'Edit',
    action: 'edit'
  },
  {
    label: 'Remove',
    action: 'remove',
    className: 'text-critical-text'
  }
];

const handleMenuAction = (action: string) => {
  switch (action) {
    case 'edit':
      router.push({
        name: 'ManageEventStopEdit',
        params: { order: props.stop.order.toString() }
      });
      break;
    case 'remove':
      emit('deleteStop', props.stop);
      break;
  }
};

const internalShowMap = ref(false);

const toggleShowMap = () => {
  internalShowMap.value = !internalShowMap.value;
};

const formatDate = (value: string) => {
  return props.showDate
    ? `${format(new Date(value), "hh:mm aaa")} on ${format(new Date(value), "MM/dd/yyyy")}`
    : format(new Date(value), "hh:mm aaa");
};
</script>